import React, { Component } from 'react';
import { Dialog, Snackbar, Box, Typography, Pill } from 'components';  
import Bugsnag from '@bugsnag/js';

class ErrorBoundary extends Component {
  constructor(props) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      showDialog: false,
      showToast: false
    };
  }

  static getDerivedStateFromError(error) {
    // Update state so the next render will show the fallback UI
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    // Report the error to Bugsnag
    Bugsnag.notify(error);
    
    // Show toast notification
    this.setState({ showToast: true });
    
    // Log the error for debugging purposes
    console.error('Error caught by error boundary:', error, errorInfo);
  }

  handleToastClose = () => {
    this.setState({ showToast: false });
  };

  handleDialogOpen = () => {
    this.setState({ showDialog: true });
  };

  handleDialogClose = () => {
    this.setState({ showDialog: false });
  };

  render() {
    // Always render toast notification when there's an error
    const errorNotifications = this.state.hasError ? (
      <>
        <Snackbar
          open={this.state.showToast}
          message="An error has occurred. Please try again later."
          onClose={this.handleToastClose}
          variant="error"
        />
        
        <Dialog
          open={this.state.showDialog}
          title="Application Error"
          body={
            <>
              <p>We apologize for the inconvenience. An error has occurred in the application.</p>
              <p>The error has been reported to our team and we're working to resolve it.</p>
              <p>You can continue using other parts of the application or try again later.</p>
            </>
          }
          confirmText="Dismiss"
          onClose={this.handleDialogClose}
        />
      </>
    ) : null;
    
    // Define inline styles for error display
    const styles = {
      errorContainer: {
        padding: 24,
        backgroundColor: 'white',
        borderRadius: 8,
        boxShadow: '0 1px 3px rgba(0, 0, 0, 0.1)',
        maxWidth: '1200px',
        margin: '0 auto',
        marginTop: 16
      },
      errorHeader: {
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 24,
        borderBottom: '1px solid #f0f0f0',
        paddingBottom: 16
      },
      errorContent: {
        padding: 24,
        backgroundColor: '#fafafa',
        borderRadius: 8
      }
    };
    
    // Don't try to re-render the component that caused the error
    if (this.state.hasError) {
      return (
        <>
          <Box style={styles.errorContainer}>
            {/* Header with navigation remains accessible */}
            <Box style={styles.errorHeader}>
              <Typography variant="h4">Error Notification</Typography>
              <Box>
                <Pill size="small" onClick={this.handleDialogOpen}>More Info</Pill>
                <Pill size="small" component="a" href="/" style={{ marginLeft: 8 }}>Home</Pill>
                <Pill size="small" component="a" href="/logout" style={{ marginLeft: 8 }}>Logout</Pill>
              </Box>
            </Box>
            
            <Box style={styles.errorContent}>
              <Typography variant="h5" style={{ marginBottom: 16 }}>Something went wrong</Typography>
              <Typography variant="body1" paragraph>
                The application encountered an error while displaying this content.
              </Typography>
              <Typography variant="body1" paragraph>
                This error has been automatically reported to our team.
                You can navigate to other sections of the application using the navigation menu.
              </Typography>
              <Box mt={3}>
                <Typography variant="body2" color="textSecondary">
                  If this problem persists, please contact support.
                </Typography>
              </Box>
            </Box>
          </Box>
          
          {errorNotifications}
        </>
      );
    }

    // Normal rendering when no errors
    return (
      <>
        {this.props.children}
        {errorNotifications}
      </>
    );
  }
}

export default ErrorBoundary;
